import {
  type SandpackCodeEditor,
  useActiveCode,
  useSandpack,
} from '@codesandbox/sandpack-react';
import { useEffect } from 'react';
import { useStableCallback } from '../use-stable-callback';

type CodeMirrorRef = React.ComponentRef<typeof SandpackCodeEditor>;

type UsePrettierKeyMapInput = {
  editorRef: React.MutableRefObject<CodeMirrorRef | null>;
};

const fileTypeToParser = {
  ts: 'babel-ts',
  tsx: 'babel-ts',
  js: 'babel',
  jsx: 'babel',
  json: 'json',
  html: 'html',
  css: 'css',
};

export const usePrettierKeyMap = ({ editorRef }: UsePrettierKeyMapInput) => {
  const { sandpack } = useSandpack();
  const { code, updateCode } = useActiveCode();

  const formatCode = async () => {
    try {
      const view = editorRef.current?.getCodemirror();
      if (!view) return;

      const [prettier, babelPlugin, htmlPlugin, cssPlugin, estreePlugin] =
        await Promise.all([
          import('prettier/standalone'),
          import('prettier/plugins/babel'),
          import('prettier/plugins/html'),
          import('prettier/plugins/postcss'),
          import('prettier/plugins/estree'),
        ]);

      const currentPosition = view.state.selection.ranges[0]?.to ?? 0;

      const [, fileType = ''] = sandpack.activeFile.split('.');
      const { formatted } = await prettier.formatWithCursor(code, {
        plugins: [
          babelPlugin.default,
          htmlPlugin.default,
          cssPlugin.default,
          estreePlugin.default,
        ],
        parser:
          fileTypeToParser[fileType as keyof typeof fileTypeToParser] ??
          'babel',
        cursorOffset: currentPosition,
      });

      const shouldUpdatePreview = true;
      updateCode(formatted, shouldUpdatePreview);
    } catch (error) {
      // TODO: Handle errors
    }
  };

  const handleSave = useStableCallback((event: KeyboardEvent) => {
    const view = editorRef.current?.getCodemirror();
    if (!view || !view.hasFocus) return;

    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      void formatCode();
    }
  });

  useEffect(() => {
    window.addEventListener('keydown', handleSave);
    return () => {
      window.removeEventListener('keydown', handleSave);
    };
  }, [handleSave]);

  return { formatCode };
};
